import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, Edit, PackageX, Plus, RefreshCw, Search, Star, Trash2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, PaginationMeta, getCurrentAdmin, logout } from "./api";
import { Product, ProductCategory, deleteAdminProduct, listAdminProductCategories, listAdminProducts } from "@/services/products";
import { useConfirmDialog } from "./components/ConfirmDialog";
import Pagination from "./components/Pagination";
import { withImageFallback } from "@/utils/images";

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

const stockStatusLabel: Record<Product["stockStatus"], string> = {
  ok: "正常",
  low: "庫存偏低",
  out: "已無庫存",
};

const stockStatusClass: Record<Product["stockStatus"], string> = {
  ok: "bg-[#e9f4ec] text-[#245438]",
  low: "bg-[#f3ecdf] text-[#8a6a2f]",
  out: "bg-[#fdebea] text-[#9a2d2d]",
};

function CategoryPathCell({ category }: { category: string }) {
  const parts = category.split(">").map((item) => item.trim()).filter(Boolean);
  if (parts.length === 0) return <span className="text-sm text-[#7b877f]">-</span>;
  const [parent, ...children] = parts;

  return (
    <div className="max-w-[240px]">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex max-w-full items-center rounded-md border border-[#dfe6e1] bg-[#f6f8f7] px-2.5 py-1 text-xs font-bold text-[#315746]">
          <span className="truncate">{parent}</span>
        </span>
        {children.map((child) => (
          <span className="inline-flex max-w-full items-center rounded-md bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]" key={child}>
            <span className="truncate">{child}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStockLevel = searchParams.get("stock") === "low" || searchParams.get("stock") === "out" ? (searchParams.get("stock") as "low" | "out") : "";
  const initialStatus = searchParams.get("status") === "Published" || searchParams.get("status") === "Hidden" ? (searchParams.get("status") as string) : "";
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [counts, setCounts] = useState({ Published: 0, Hidden: 0 });
  const [stockCounts, setStockCounts] = useState({ Low: 0, Out: 0 });
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState(initialStatus);
  const [categoryId, setCategoryId] = useState("");
  const [keyword, setKeyword] = useState("");
  const [stockLevel, setStockLevel] = useState<"" | "low" | "out">(initialStockLevel);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load(
    filters: { Status: string; CategoryId: string; Keyword: string; StockLevel?: "low" | "out"; Page: number; PerPage: number } = {
      Status: status,
      CategoryId: categoryId,
      Keyword: keyword,
      StockLevel: stockLevel || undefined,
      Page: pagination.Page,
      PerPage: pagination.PerPage,
    },
  ) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const [data, categoryRows] = await Promise.all([listAdminProducts(filters), listAdminProductCategories()]);
      setProducts(data.Products);
      setCounts(data.Counts || { Published: 0, Hidden: 0 });
      setStockCounts(data.StockCounts || { Low: 0, Out: 0 });
      setCategories(categoryRows.filter((item) => item.isActive));
      setPagination(data.Pagination || defaultPagination);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "產品資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: initialStatus, CategoryId: "", Keyword: "", StockLevel: initialStockLevel || undefined, Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: 1, PerPage: pagination.PerPage });
  }

  async function handleDelete(product: Product) {
    const confirmed = await confirm({
      title: "刪除產品？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{product.name}</strong> 嗎？
          <br />
          刪除後商品資料與圖片將無法復原。
        </>
      ),
      confirmText: "刪除產品",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminProduct(product.id);
      await load({ Status: status, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "產品刪除失敗。");
    }
  }

  const allProductCount = counts.Published + counts.Hidden || pagination.Total;

  function categoryOptionLabel(category: ProductCategory) {
    const parent = categories.find((item) => item.id === category.parentCategoryId);
    return parent ? `${parent.name} > ${category.name}` : category.name;
  }

  return (
    <AdminLayout sectionLabel="產品管理" title="產品列表" user={currentUser} onLogout={handleLogout}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2 overflow-x-auto pb-1 md:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { value: "", label: "全部產品", count: allProductCount, tone: "text-[#315746]" },
            { value: "Published", label: "上架中", count: counts.Published, tone: "text-[#245438]" },
            { value: "Hidden", label: "下架", count: counts.Hidden, tone: "text-[#59665f]" },
          ].map((item) => (
            <button
              className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${
                status === item.value ? "border-[#315746] bg-[#315746] text-white" : `border-[#dfe6e1] bg-white ${item.tone}`
              }`}
              key={item.value}
              onClick={() => {
                setStatus(item.value);
                load({ Status: item.value, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: 1, PerPage: pagination.PerPage });
              }}
              type="button"
            >
              <span className="text-xs font-bold">{item.label}</span>
              <span className="ml-3 text-lg font-bold">{item.count}</span>
            </button>
          ))}
        </div>

        <div className="hidden gap-3 md:grid md:flex-1 md:grid-cols-3">
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus(""); load({ Status: "", CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">全部產品</p>
            <p className="mt-1 text-2xl font-bold text-[#203d32]">{allProductCount}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus("Published"); load({ Status: "Published", CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">上架中</p>
            <p className="mt-1 text-2xl font-bold text-[#245438]">{counts.Published}</p>
          </button>
          <button className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-left hover:border-[#b8c8bf]" onClick={() => { setStatus("Hidden"); load({ Status: "Hidden", CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <p className="text-xs font-bold text-[#7b877f]">下架</p>
            <p className="mt-1 text-2xl font-bold text-[#59665f]">{counts.Hidden}</p>
          </button>
        </div>
      </div>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {[
          { value: "" as const, label: "全部庫存", count: null, icon: null },
          { value: "low" as const, label: "庫存偏低", count: stockCounts.Low, icon: <AlertTriangle size={14} /> },
          { value: "out" as const, label: "已無庫存", count: stockCounts.Out, icon: <PackageX size={14} /> },
        ].map((item) => (
          <button
            className={`flex min-w-[120px] shrink-0 items-center justify-between gap-2 rounded-md border px-3 py-2 text-left transition ${
              stockLevel === item.value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#8a6a2f]"
            }`}
            key={item.value || "all"}
            onClick={() => {
              setStockLevel(item.value);
              load({ Status: status, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: item.value || undefined, Page: 1, PerPage: pagination.PerPage });
            }}
            type="button"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-bold">
              {item.icon}
              {item.label}
            </span>
            {item.count !== null && <span className="text-lg font-bold">{item.count}</span>}
          </button>
        ))}
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:sticky md:top-[102px] md:z-10 md:flex md:flex-wrap md:items-end md:bg-white/95 md:p-4 md:shadow-[0_10px_24px_rgba(32,61,50,.08)] md:backdrop-blur" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="product-status">狀態</label>
          <select id="product-status" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Published">上架中</option>
            <option value="Hidden">下架</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[150px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="product-category">系列</label>
          <select id="product-category" className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
            <option value="">全部系列</option>
            {categories.map((item) => <option key={item.id} value={item.id}>{categoryOptionLabel(item)}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[140px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="product-stock-level">庫存狀態</label>
          <select
            id="product-stock-level"
            className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            value={stockLevel}
            onChange={(event) => setStockLevel(event.target.value as "" | "low" | "out")}
          >
            <option value="">全部</option>
            <option value="low">庫存偏低</option>
            <option value="out">已無庫存</option>
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[260px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="product-keyword">關鍵字</label>
          <input id="product-keyword" className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="產品名稱、編號、條碼、Slug、系列、描述" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]">
          <Search size={18} />
          搜尋
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load({ Status: status, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/admin/products/new")} type="button">
          <Plus size={20} />
          新增產品
        </button>
      </form>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1480px] table-fixed border-separate border-spacing-0 text-left">
            <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[300px] px-4 py-3">產品</th>
                <th className="w-[130px] px-4 py-3">編號</th>
                <th className="w-[220px] px-4 py-3">系列</th>
                <th className="w-[110px] px-4 py-3">類型</th>
                <th className="w-[140px] px-4 py-3">價格</th>
                <th className="w-[92px] px-4 py-3">狀態</th>
                <th className="w-[110px] px-4 py-3">庫存</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[150px] px-4 py-3">更新時間</th>
                <th className="sticky right-0 z-50 w-[160px] bg-[#f0f3f1] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={10}>載入中...</td></tr>
              ) : products.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={10}>沒有符合條件的產品。</td></tr>
              ) : products.map((product) => (
                <tr className="border-t border-[#edf0ee]" key={product.id}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <img className="h-14 w-14 rounded-md border border-[#edf0ee] object-cover" src={withImageFallback(product.image)} alt={product.name} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-[#203d32]">{product.name}</p>
                          {product.isFeatured && <Star className="fill-[#d4a574] text-[#d4a574]" size={16} />}
                        </div>
                        <p className="mt-1 max-w-[320px] truncate text-sm text-[#59665f]">{product.subtitle}</p>
                        {product.hasVariants && (
                          <span className="mt-1 inline-flex items-center rounded-full bg-[#f0f3f1] px-2 py-0.5 text-[11px] font-bold text-[#59665f]">
                            多規格 · {product.variantCount} 組
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-bold text-[#203d32]">{product.productCode || "-"}</div>
                    {product.barcode && <div className="mt-1 text-xs text-[#7b877f]">{product.barcode}</div>}
                  </td>
                  <td className="px-4 py-4"><CategoryPathCell category={product.category} /></td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${product.productType === "Display" ? "bg-[#f3ecdf] text-[#8a6a2f]" : "bg-[#e9f4ec] text-[#245438]"}`}>
                      {product.productType === "Display" ? "展示型" : "銷售型"}
                    </span>
                  </td>
                  <td className="px-4 py-4 font-bold">
                    {product.salePrice ? (
                      <div>
                        <div className="text-[#9a2d2d]">NT$ {product.salePrice.toLocaleString()}</div>
                        <div className="mt-1 text-xs font-normal text-[#7b877f] line-through">NT$ {product.price.toLocaleString()}</div>
                      </div>
                    ) : (
                      <>NT$ {product.price.toLocaleString()}</>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${product.isPublished === false ? "bg-[#eeeeee] text-[#59635d]" : "bg-[#e9f4ec] text-[#245438]"}`}>
                      {product.isPublished === false ? "下架" : "上架"}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${stockStatusClass[product.stockStatus]}`}>
                      {stockStatusLabel[product.stockStatus]}
                    </span>
                    {!product.hasVariants && product.stock !== null && (
                      <div className="mt-1 text-xs text-[#7b877f]">庫存 {product.stock}</div>
                    )}
                  </td>
                  <td className="px-4 py-4">{product.sortOrder || 0}</td>
                  <td className="px-4 py-4 text-sm text-[#59665f]">{product.updatedAt || "-"}</td>
                  <td className="sticky right-0 z-10 bg-white px-4 py-4 shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate(`/admin/products/${product.id}/edit`)} type="button">
                        <Edit size={16} />編輯
                      </button>
                      <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => handleDelete(product)} type="button">
                        <Trash2 size={16} />刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, CategoryId: categoryId, Keyword: keyword.trim(), StockLevel: stockLevel || undefined, Page: page, PerPage: pagination.PerPage })} />
      </div>
      <ConfirmDialog />
    </AdminLayout>
  );
}
